"use client";

import * as React from "react";
import { Autocomplete, AutocompleteOption } from "@/components/ui/autocomplete";
import { searchABNByName, ABNEntity, isValidABN, isValidACN } from "@/lib/abn-lookup";
import { useDebounce } from "@/hooks/useDebounce";
import { formatABN } from "@/lib/abn-lookup";
import { cn } from "@/lib/utils";

interface ABNAutocompleteProps {
  value: string;
  onValueChange: (value: string, entity?: ABNEntity) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function ABNAutocomplete({
  value,
  onValueChange,
  placeholder = "Search by Business Name, ABN (11 digits), or ACN (9 digits)",
  className,
  disabled,
}: ABNAutocompleteProps) {
  const [options, setOptions] = React.useState<AutocompleteOption[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [entityData, setEntityData] = React.useState<Map<string, ABNEntity>>(new Map());

  // Debounce search query to avoid too many API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Search function
  const performSearch = React.useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setOptions([]);
      return;
    }

    setLoading(true);

    try {
      // Check if it's an ABN or ACN format
      const cleanQuery = query.replace(/\s/g, '');
      
      // Determine search type for better user experience
      let searchType = 'business';
      if (isValidABN(cleanQuery)) {
        searchType = 'ABN';
      } else if (isValidACN(cleanQuery)) {
        searchType = 'ACN';
      }

      // Search with just the query - all configuration happens server-side
      const entities = await searchABNByName(query);

      // Convert entities to autocomplete options
      const newOptions: AutocompleteOption[] = [];
      const newEntityData = new Map<string, ABNEntity>();
      const seenNames = new Set<string>(); // Track seen names to avoid duplicates

      entities.forEach((entity) => {
        // Create options for business names (main business names)
        if (entity.businessName && Array.isArray(entity.businessName)) {
          entity.businessName.forEach((name) => {
            const cleanName = name.trim();
            const optionValue = `${entity.abn || ''}-business-${cleanName}`;
            
            // Skip if we've already seen this exact name for this ABN
            if (seenNames.has(optionValue)) {
              return;
            }
            
            seenNames.add(optionValue);
            newOptions.push({
              value: optionValue,
              label: cleanName,
              description: `ABN: ${entity.abn || ''} • ${entity.mainBusinessLocation?.state || 'Unknown'}`,
              metadata: {
                type: 'business',
                abn: entity.abn || '',
                entityType: entity.entityType || '',
                state: entity.mainBusinessLocation?.state || '',
                postcode: entity.mainBusinessLocation?.postcode || '',
                searchType,
              },
            });
            newEntityData.set(optionValue, entity);
          });
        }

        // Create options for trading names
        if (entity.tradingName && Array.isArray(entity.tradingName)) {
          entity.tradingName.forEach((name) => {
            const cleanName = name.trim();
            const optionValue = `${entity.abn || ''}-trading-${cleanName}`;
            
            // Skip if we've already seen this exact name for this ABN
            if (seenNames.has(optionValue)) {
              return;
            }
            
            seenNames.add(optionValue);
            newOptions.push({
              value: optionValue,
              label: cleanName,
              description: `ABN: ${entity.abn || ''} • Trading Name • ${entity.mainBusinessLocation?.state || 'Unknown'}`,
              metadata: {
                type: 'trading',
                abn: entity.abn || '',
                entityType: entity.entityType || '',
                state: entity.mainBusinessLocation?.state || '',
                postcode: entity.mainBusinessLocation?.postcode || '',
                searchType,
              },
            });
            newEntityData.set(optionValue, entity);
          });
        }

        // Create option for legal name if no business names
        if (!entity.businessName?.length && entity.legalName) {
          const cleanName = entity.legalName.trim();
          const optionValue = `${entity.abn || ''}-legal-${cleanName}`;
          
                      // Skip if we've already seen this exact name for this ABN
            if (seenNames.has(optionValue)) {
              return;
            }
          
          seenNames.add(optionValue);
          newOptions.push({
            value: optionValue,
            label: cleanName,
            description: `ABN: ${entity.abn || ''} • Legal Name • ${entity.mainBusinessLocation?.state || 'Unknown'}`,
            metadata: {
              type: 'legal',
              abn: entity.abn || '',
              entityType: entity.entityType || '',
              state: entity.mainBusinessLocation?.state || '',
              postcode: entity.mainBusinessLocation?.postcode || '',
              searchType,
            },
          });
          newEntityData.set(optionValue, entity);
        }
      });

      setOptions(newOptions);
      setEntityData(newEntityData);
      
    } catch (error) {
      console.error('ABN search error:', error);
      setOptions([]);
      // You might want to show a toast notification here
    } finally {
      setLoading(false);
    }
  }, []);

  // Effect to trigger search when debounced query changes
  React.useEffect(() => {
    if (debouncedSearchQuery) {
      performSearch(debouncedSearchQuery);
    } else {
      setOptions([]);
    }
  }, [debouncedSearchQuery, performSearch]);



  // Handle search query change
  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Handle value selection
  const handleValueChange = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (selectedValue: string, option?: AutocompleteOption) => {
      const entity = entityData.get(selectedValue);
      onValueChange?.(selectedValue, entity);
    },
    [entityData, onValueChange]
  );

  // Custom option renderer
  const renderOption = React.useCallback((option: AutocompleteOption) => {
    const metadata = option.metadata;
    return (
      <div className="flex items-start p-2">
        <div className="paragraph-base min-w-0 flex-1">
          <div className="">{option.label}</div>
          <div className="flex items-center space-x-2 text-muted-foreground">
            <span>ABN: {formatABN(metadata?.abn || "")}</span>
            {metadata?.state && (
              <>
                <div className="flex items-center space-x-1">
                  <span className='material-symbols-outlined'>location_on</span>
                  {metadata.postcode && <span>{metadata.postcode}</span>}
                  <span>{metadata.state}</span>
                </div>
              </>
            )}
            
          </div>
        </div>
      </div>
    );
  }, []);

  return (
    <div className="relative">
      <Autocomplete
        options={options}
        value={value}
        onValueChange={handleValueChange}
        onSearch={handleSearch}
        placeholder={placeholder}
        emptyText="No businesses found. Try searching by business name, ABN (11 digits), or ACN (9 digits)."
        loading={loading}
        disabled={disabled}
        className={cn(
          disabled && "bg-muted",
          className
        )}
        allowCustomValue={false}
        renderOption={renderOption}
      />
      {disabled && (
        <div className="absolute inset-0 bg-muted/20 rounded-md pointer-events-none" />
      )}
    </div>
  );
}
